import Dexie, { type Table } from 'dexie'
import type {
  CachedIssue,
  DailyIssue,
  InterestValue,
  ItemReference,
  ItemState,
  IssueItem,
  VocabularyEntry,
  VocabularyGroup,
  VocabularyLookupStatus,
  VocabularyOrigin,
  VocabularyState,
  WeeklyIssue,
} from '../types/daily'

class DailyReadDatabase extends Dexie {
  issues!: Table<CachedIssue, string>
  itemStates!: Table<ItemState, string>
  vocabulary!: Table<VocabularyState, string>

  constructor() {
    super('daily-read')
    this.version(1).stores({
      issues: '&issueId,date,storedAt',
      itemStates: '&key,issueId,itemId,updatedAt',
      vocabulary: '&key,issueId,itemId,normalizedTerm,lastOpenedAt',
    })
  }
}

export const db = new DailyReadDatabase()
export const stateChangedEvent = 'daily-read-state-changed'

const notifyStateChanged = () => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(stateChangedEvent))
}
const itemStateKey = (issueId: string, itemId: string) => `${issueId}:${itemId}`
export const normalizeVocabularyTerm = (term: string) => term
  .normalize('NFKC')
  .replaceAll('’', "'")
  .replaceAll('–', '-')
  .replaceAll('—', '-')
  .trim()
  .replace(/^[\[\]{}()<>“”"'.,:;!?]+|[\[\]{}()<>“”"'.,:;!?]+$/g, '')
  .replace(/\s+/g, ' ')
  .toLocaleLowerCase('en')
const normalizeTerm = normalizeVocabularyTerm
const vocabularyKey = (issueId: string, itemId: string, term: string) => `${issueId}:${itemId}:${normalizeTerm(term)}`
const newestIso = (values: Array<string | undefined>) => values.filter((value): value is string => Boolean(value)).sort().at(-1)
const oldestIso = (values: string[]) => [...values].sort()[0]

export const createItemReference = (issueDate: string, item: IssueItem): ItemReference => ({
  issueDate,
  title: item.title,
  source: {
    name: item.source.name,
    url: item.source.url,
    language: item.source.language,
    publishedAt: item.source.publishedAt,
  },
  topics: [...item.topics],
  region: item.region,
  estimatedMinutes: item.estimatedMinutes,
  summary: item.summary,
  featuredEnglish: item.featuredEnglish,
  discussion: { ...item.discussion },
})

const enrichState = (state: ItemState, issueDate: string, item: IssueItem): ItemState => {
  const next: ItemState = {
    ...state,
    issueDate,
    reference: createItemReference(issueDate, item),
  }
  if (state.saved) {
    next.savedAt = state.savedAt ?? state.updatedAt
    next.savedSnapshot = item
  }
  return next
}

export async function initializeLocalDataCompatibility() {
  await db.transaction('rw', db.issues, db.itemStates, db.vocabulary, async () => {
    const [cachedIssues, itemStates, vocabulary] = await Promise.all([
      db.issues.toArray(),
      db.itemStates.toArray(),
      db.vocabulary.toArray(),
    ])
    const itemLookup = new Map<string, { issueDate: string; item: IssueItem }>()
    for (const cached of cachedIssues) {
      if (cached.payload.issueType !== 'daily') continue
      for (const item of cached.payload.items) {
        itemLookup.set(itemStateKey(cached.issueId, item.id), { issueDate: cached.date, item })
      }
    }

    const enrichedStates = itemStates.map((state) => {
      const match = itemLookup.get(state.key)
      return match ? enrichState(state, match.issueDate, match.item) : state
    })
    if (enrichedStates.length > 0) await db.itemStates.bulkPut(enrichedStates)

    const stateLookup = new Map(enrichedStates.map((state) => [state.key, state]))
    const groups = new Map<string, VocabularyState[]>()
    for (const entry of vocabulary) {
      const group = groups.get(entry.normalizedTerm) ?? []
      group.push(entry)
      groups.set(entry.normalizedTerm, group)
    }

    const normalizedEntries: VocabularyState[] = []
    for (const entries of groups.values()) {
      const saved = entries.some((entry) => entry.saved)
      const reviewCount = Math.max(0, ...entries.map((entry) => entry.reviewCount ?? 0))
      const lastReviewedAt = newestIso(entries.map((entry) => entry.lastReviewedAt))
      for (const entry of entries) {
        const itemMatch = itemLookup.get(itemStateKey(entry.issueId, entry.itemId))
        normalizedEntries.push({
          ...entry,
          saved,
          reviewCount,
          lastReviewedAt,
          itemReference: entry.itemReference
            ?? stateLookup.get(itemStateKey(entry.issueId, entry.itemId))?.reference
            ?? (itemMatch ? createItemReference(itemMatch.issueDate, itemMatch.item) : undefined),
        })
      }
    }
    if (normalizedEntries.length > 0) await db.vocabulary.bulkPut(normalizedEntries)
  })
}

export async function cacheIssue(issue: DailyIssue) {
  await db.transaction('rw', db.issues, db.itemStates, async () => {
    await db.issues.put({ issueId: issue.issueId, date: issue.date, payload: issue, storedAt: new Date().toISOString() })
    const existingStates = await db.itemStates.where('issueId').equals(issue.issueId).toArray()
    const items = new Map(issue.items.map((item) => [item.id, item]))
    const enriched = existingStates.flatMap((state) => {
      const item = items.get(state.itemId)
      return item ? [enrichState(state, issue.date, item)] : []
    })
    if (enriched.length > 0) await db.itemStates.bulkPut(enriched)

    const records = (await db.issues.orderBy('date').reverse().toArray()).filter((record) => record.payload.issueType === 'daily')
    if (records.length > 2) await db.issues.bulkDelete(records.slice(2).map((record) => record.issueId))
  })
}

export async function getLatestCachedIssue() {
  return (await db.issues.orderBy('date').reverse().toArray()).find((record) => record.payload.issueType === 'daily')?.payload as DailyIssue | undefined
}

export async function cacheWeeklyIssue(issue: WeeklyIssue) {
  await db.transaction('rw', db.issues, async () => {
    await db.issues.put({ issueId: issue.issueId, date: issue.weekEnd, payload: issue, storedAt: new Date().toISOString() })
    const records = (await db.issues.orderBy('date').reverse().toArray()).filter((record) => record.payload.issueType === 'weekly')
    if (records.length > 2) await db.issues.bulkDelete(records.slice(2).map((record) => record.issueId))
  })
}

export async function getLatestCachedWeeklyIssue() {
  return (await db.issues.orderBy('date').reverse().toArray()).find((record) => record.payload.issueType === 'weekly')?.payload as WeeklyIssue | undefined
}

export async function getItemStates(issueId: string) {
  return db.itemStates.where('issueId').equals(issueId).toArray()
}

export async function getAllItemStates() {
  return db.itemStates.toArray()
}

async function getOrCreateItemState(issueId: string, issueDate: string, item: IssueItem): Promise<ItemState> {
  const key = itemStateKey(issueId, item.id)
  const current = await db.itemStates.get(key)
  if (current) return enrichState(current, issueDate, item)
  return {
    key,
    issueId,
    itemId: item.id,
    issueDate,
    reference: createItemReference(issueDate, item),
    saved: false,
    interest: null,
    updatedAt: new Date().toISOString(),
  }
}

export async function markItemOpened(issueId: string, issueDate: string, item: IssueItem) {
  const current = await getOrCreateItemState(issueId, issueDate, item)
  const now = new Date().toISOString()
  const next = { ...current, firstOpenedAt: current.firstOpenedAt ?? now, lastOpenedAt: now, updatedAt: now }
  await db.itemStates.put(next)
  notifyStateChanged()
  return next
}

export async function setItemSaved(issueId: string, issueDate: string, item: IssueItem, saved: boolean) {
  const current = await getOrCreateItemState(issueId, issueDate, item)
  const now = new Date().toISOString()
  const next: ItemState = { ...current, saved, updatedAt: now }
  if (saved) {
    next.savedAt = current.savedAt ?? now
    next.savedSnapshot = item
  } else {
    delete next.savedAt
    delete next.savedSnapshot
  }
  await db.itemStates.put(next)
  notifyStateChanged()
  return next
}

export async function setItemInterest(issueId: string, issueDate: string, item: IssueItem, interest: InterestValue) {
  const current = await getOrCreateItemState(issueId, issueDate, item)
  const next = { ...current, interest, updatedAt: new Date().toISOString() }
  await db.itemStates.put(next)
  notifyStateChanged()
  return next
}

export async function setExistingItemInterest(key: string, interest: InterestValue) {
  const current = await db.itemStates.get(key)
  if (!current) return undefined
  const next = { ...current, interest, updatedAt: new Date().toISOString() }
  await db.itemStates.put(next)
  notifyStateChanged()
  return next
}

export async function setExistingItemSaved(key: string, saved: boolean) {
  const current = await db.itemStates.get(key)
  if (!current) return undefined
  if (saved && !current.savedSnapshot) return undefined
  const next: ItemState = { ...current, saved, updatedAt: new Date().toISOString() }
  if (saved) next.savedAt = current.savedAt ?? new Date().toISOString()
  else {
    delete next.savedAt
    delete next.savedSnapshot
  }
  await db.itemStates.put(next)
  notifyStateChanged()
  return next
}

export interface VocabularyRecordMetadata {
  normalizedTerm?: string
  surfaceNormalized?: string
  origin?: VocabularyOrigin
  lookupStatus?: VocabularyLookupStatus
  contextExcerpt?: string
  dictionaryVersion?: string
  saved?: boolean
}

export async function recordVocabulary(
  issueId: string,
  issueDate: string,
  item: IssueItem,
  vocabulary: VocabularyEntry,
  metadata: VocabularyRecordMetadata = {},
) {
  const normalizedTerm = normalizeTerm(metadata.normalizedTerm ?? vocabulary.lemma ?? vocabulary.term)
  const surfaceNormalized = normalizeTerm(metadata.surfaceNormalized ?? vocabulary.term)
  const key = vocabularyKey(issueId, item.id, normalizedTerm)
  const [current, group] = await Promise.all([
    db.vocabulary.get(key),
    db.vocabulary.where('normalizedTerm').equals(normalizedTerm).toArray(),
  ])
  const now = new Date().toISOString()
  const saved = metadata.saved ?? (group.some((entry) => entry.saved) || (current?.saved ?? false))
  const reviewCount = Math.max(0, ...group.map((entry) => entry.reviewCount ?? 0))
  const lastReviewedAt = newestIso(group.map((entry) => entry.lastReviewedAt))
  const next: VocabularyState = {
    ...vocabulary,
    key,
    issueId,
    itemId: item.id,
    normalizedTerm,
    surfaceNormalized,
    origin: metadata.origin ?? current?.origin ?? 'curated',
    lookupStatus: metadata.lookupStatus ?? current?.lookupStatus ?? 'resolved',
    contextExcerpt: metadata.contextExcerpt ?? current?.contextExcerpt,
    dictionaryVersion: metadata.dictionaryVersion ?? current?.dictionaryVersion,
    firstOpenedAt: current?.firstOpenedAt ?? now,
    lastOpenedAt: now,
    saved,
    reviewCount,
    lastReviewedAt,
    itemReference: createItemReference(issueDate, item),
  }
  await db.vocabulary.put(next)
  notifyStateChanged()
  return next
}

export async function findResolvedVocabulary(surfaceTerm: string) {
  const surfaceNormalized = normalizeTerm(surfaceTerm)
  const entries = await db.vocabulary
    .filter((entry) => (entry.lookupStatus ?? 'resolved') === 'resolved'
      && (entry.surfaceNormalized === surfaceNormalized || entry.normalizedTerm === surfaceNormalized))
    .toArray()
  return entries.sort((left, right) => right.lastOpenedAt.localeCompare(left.lastOpenedAt))[0]
}

export async function resolvePendingVocabulary(normalizedTerm: string, vocabulary: VocabularyEntry, metadata: VocabularyRecordMetadata = {}) {
  const entries = await db.vocabulary.where('normalizedTerm').equals(normalizedTerm).toArray()
  if (entries.length === 0) return undefined
  const targetTerm = normalizeTerm(metadata.normalizedTerm ?? vocabulary.lemma ?? vocabulary.term)
  const targetGroup = targetTerm === normalizedTerm ? [] : await db.vocabulary.where('normalizedTerm').equals(targetTerm).toArray()
  const saved = [...entries, ...targetGroup].some((entry) => entry.saved)
  const reviewCount = Math.max(0, ...[...entries, ...targetGroup].map((entry) => entry.reviewCount ?? 0))
  const lastReviewedAt = newestIso([...entries, ...targetGroup].map((entry) => entry.lastReviewedAt))
  const updated = entries.map((entry) => ({
    ...entry,
    ...vocabulary,
    normalizedTerm: targetTerm,
    surfaceNormalized: entry.surfaceNormalized ?? normalizeTerm(entry.term),
    origin: metadata.origin ?? 'dictionary' as VocabularyOrigin,
    lookupStatus: 'resolved' as VocabularyLookupStatus,
    dictionaryVersion: metadata.dictionaryVersion,
    saved,
    reviewCount,
    lastReviewedAt,
  }))
  await db.vocabulary.bulkPut(updated)
  if (targetGroup.length > 0) {
    await db.vocabulary.bulkPut(targetGroup.map((entry) => ({ ...entry, saved, reviewCount, lastReviewedAt })))
  }
  notifyStateChanged()
  return updated[0]
}

export async function setVocabularySaved(normalizedTerm: string, saved: boolean) {
  const entries = await db.vocabulary.where('normalizedTerm').equals(normalizedTerm).toArray()
  if (entries.length === 0) return undefined
  const updated = entries.map((entry) => ({ ...entry, saved }))
  await db.vocabulary.bulkPut(updated)
  notifyStateChanged()
  return updated.find((entry) => entry.key === entries[0].key) ?? updated[0]
}

export async function markVocabularyReviewed(normalizedTerm: string) {
  const entries = await db.vocabulary.where('normalizedTerm').equals(normalizedTerm).toArray()
  if (entries.length === 0 || entries.every((entry) => (entry.lookupStatus ?? 'resolved') === 'pending')) return undefined
  const now = new Date().toISOString()
  const reviewCount = Math.max(0, ...entries.map((entry) => entry.reviewCount ?? 0)) + 1
  const updated = entries.map((entry) => ({ ...entry, reviewCount, lastReviewedAt: now }))
  await db.vocabulary.bulkPut(updated)
  notifyStateChanged()
  return { reviewCount, lastReviewedAt: now }
}

export async function getVocabularyGroups(): Promise<VocabularyGroup[]> {
  const entries = await db.vocabulary.toArray()
  const grouped = new Map<string, VocabularyState[]>()
  for (const entry of entries) {
    const group = grouped.get(entry.normalizedTerm) ?? []
    group.push(entry)
    grouped.set(entry.normalizedTerm, group)
  }
  return Array.from(grouped.entries()).map(([normalizedTerm, occurrences]) => {
    const sortedOccurrences = [...occurrences].sort((left, right) => right.lastOpenedAt.localeCompare(left.lastOpenedAt))
    const latest = sortedOccurrences[0]
    return {
      normalizedTerm,
      term: latest.term,
      difficulties: Array.from(new Set(occurrences.map((entry) => entry.difficulty))),
      pending: occurrences.every((entry) => (entry.lookupStatus ?? 'resolved') === 'pending'),
      saved: occurrences.some((entry) => entry.saved),
      firstOpenedAt: oldestIso(occurrences.map((entry) => entry.firstOpenedAt)),
      lastOpenedAt: latest.lastOpenedAt,
      reviewCount: Math.max(0, ...occurrences.map((entry) => entry.reviewCount ?? 0)),
      lastReviewedAt: newestIso(occurrences.map((entry) => entry.lastReviewedAt)),
      occurrences: sortedOccurrences,
    }
  }).sort((left, right) => right.lastOpenedAt.localeCompare(left.lastOpenedAt))
}

export async function getVocabularyGroupsForDateRange(start: string, end: string) {
  return (await getVocabularyGroups()).filter((group) => group.occurrences.some((entry) => {
    const issueDate = entry.itemReference?.issueDate
    return Boolean(issueDate && issueDate >= start && issueDate <= end)
  }))
}

export async function getWeeklyLocalCounts(start: string, end: string) {
  const [states, vocabularyGroups] = await Promise.all([db.itemStates.toArray(), getVocabularyGroupsForDateRange(start, end)])
  return {
    openedItems: states.filter((state) => Boolean(state.firstOpenedAt && state.issueDate && state.issueDate >= start && state.issueDate <= end)).length,
    vocabulary: vocabularyGroups.length,
    reviewableVocabulary: vocabularyGroups.filter((group) => !group.pending).length,
  }
}

export async function getLocalCounts() {
  const [itemStates, vocabularyGroups] = await Promise.all([db.itemStates.toArray(), getVocabularyGroups()])
  return {
    savedItems: itemStates.filter((item) => item.saved).length,
    feedbackItems: itemStates.filter((item) => Boolean(item.interest)).length,
    readingRecords: itemStates.filter((item) => item.firstOpenedAt).length,
    vocabulary: vocabularyGroups.length,
    pendingVocabulary: vocabularyGroups.filter((item) => item.pending).length,
    reviewedVocabulary: vocabularyGroups.filter((item) => item.reviewCount > 0).length,
  }
}
